/**
 * LocationMap — small Leaflet preview that animates in once the applicant
 * picks a work location.  Shows a CircleMarker over the store with the
 * store's display name.  Uses lat/lng provided by the public locations
 * endpoint (falls back to "no map available" if the location row is
 * missing coordinates).
 */
import React from 'react';
import { motion } from 'framer-motion';
import { MapContainer, TileLayer, CircleMarker, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Storefront } from '@phosphor-icons/react';

export function LocationMap({ location }) {
  const lat = Number(location?.lat);
  const lng = Number(location?.lng);
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
  return (
    <motion.div
      key={location?.name || 'empty'}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.2, 0.9, 0.3, 1] }}
      className="rounded-2xl overflow-hidden"
      style={{ background: 'var(--cs-bg-surface-2)', border: '1px solid var(--cs-border)' }}
      data-testid="location-map">
      {hasCoords ? (
        <div className="relative">
          <MapContainer
            center={[lat, lng]}
            zoom={14}
            scrollWheelZoom={false}
            dragging={false}
            doubleClickZoom={false}
            zoomControl={false}
            attributionControl={false}
            style={{ height: 160, width: '100%' }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <CircleMarker
              center={[lat, lng]}
              radius={11}
              pathOptions={{
                color: '#fff',
                weight: 3,
                fillColor: '#CD040B',
                fillOpacity: 1,
              }}>
              <Tooltip direction="top" offset={[0, -8]} permanent>
                <span style={{ fontWeight: 900, fontSize: 11 }}>{location.name}</span>
              </Tooltip>
            </CircleMarker>
          </MapContainer>
          <div className="absolute bottom-2 right-2 px-2 py-1 rounded text-[9px] font-black uppercase tracking-wider"
            style={{ background: 'rgba(0,0,0,0.62)', color: '#fff', backdropFilter: 'blur(4px)' }}>
            <MapPin size={9} weight="fill" className="inline mr-1" />
            Your store
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 px-4 py-5"
          style={{ background: 'linear-gradient(135deg, #10B98115, transparent)' }}>
          <span className="w-10 h-10 rounded-xl inline-flex items-center justify-center shrink-0"
            style={{ background: 'linear-gradient(135deg, #10B981, #06B6D4)' }}>
            <Storefront size={18} weight="fill" style={{ color: '#0B0B0F' }} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-black truncate" style={{ color: 'var(--cs-text)' }}>
              {location?.name}
            </div>
            <div className="text-[11px]" style={{ color: 'var(--cs-text-muted)' }}>
              We&rsquo;ve noted your preference. (Map preview unavailable.)
            </div>
          </div>
        </div>
      )}
      {(location?.address || location?.phone) && hasCoords && (
        <div className="px-4 py-2.5 text-[11px] flex items-center gap-3 flex-wrap"
          style={{ borderTop: '1px solid var(--cs-border)', color: 'var(--cs-text-muted)' }}>
          {location.address && (
            <span className="inline-flex items-center gap-1">
              <MapPin size={10} weight="fill" /> {location.address}
            </span>
          )}
          {location.phone && (
            <span className="font-mono">{location.phone}</span>
          )}
        </div>
      )}
    </motion.div>
  );
}
